// React
import React, { FC, useContext, useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
// This will need to be dealt with later, installing this
// broke the app
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';

// Styles
import injectSheet from 'react-jss';

// Components
import { Loading } from 'Components/Loading';

// Containers
import { GoodDataDashboardContainer } from 'Containers/GoodData/containers';

// Actions
import { setPlatformInfoEventValue } from 'Containers/GoodData/actions/goodData.actions';

// HOC
import Card from 'HOC/Card';

// Utilities
import {
  modifyGoodDataFilterContext,
  isUserOnOrPastSafariTwelve,
} from 'Containers/GoodData/utilities';
import { areEqual } from 'Utils/areEqual';
import withErrorBoundary from 'HOC/ErrorBoundary';
import styleSheet from './styles';
import goodDataGql from './gqlQuery';

interface Props {
  classes?: any;
  audienceKey: string;
}

const ProfileDetails: FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const { platformInfoEvent, userBrowser } = useSelector(
    (selectors: any) => selectors.goodData,
    areEqual
  );

  // State that holds the GraphQL data.
  const [gdGQLData, setGDGQLData] = useState(undefined);

  // Use this state to keep track of how many label.value.change
  // events have occured in order to know how long to display our
  // loading animation
  const [isLoading, setIsLoading] = useState(true);

  const { classes, audienceKey } = props;

  // This constant is the dashboard name as the back-end knows it.
  const PROFILE_DETAILS_DASHBOARD_NAME = 'profile_details';

  // This constant is the label that is modified in modifyGoodDataFilterContext
  // calls for this dashboard.  Called upon platform.info event to pass in which user to
  // filter the dashboard to.
  const USER_FILTER_LABEL =
    'label.profile_details_summary.user_key_consolidated';

  const INST_FILTER_LABEL =
    'label.cp_institution_daily_summary_with_network.institution_id';

  const userDetails = useContext(UserDetailsContext);

  // GraphQL User.GoodDataClaims Query
  const { data: goodDataData } = useQuery(goodDataGql, {
    variables: {
      id: userDetails.userId,
      accountId: userDetails.accountId,
      base64Encoded: true,
      instId: userDetails.instId,
    },
    skip: gdGQLData,
  });

  // End loading early if we know there is nothing loading for
  // aesthetic purposes
  useEffect(() => {
    if (userBrowser && isUserOnOrPastSafariTwelve(userBrowser)) {
      setIsLoading(false);
    }
  }, [userBrowser]);

  // useEffect to update state when the goodDataData changes.  Should
  // really only happen once.
  useEffect(() => {
    if (goodDataData && !areEqual(goodDataData, gdGQLData)) {
      setGDGQLData(goodDataData);
    }
  }, [goodDataData]);

  // The first time the dashboard has loaded, change the filter context to use the audienceKey as filter
  // Product requirement).
  useEffect(() => {
    if (platformInfoEvent) {
      modifyGoodDataFilterContext(PROFILE_DETAILS_DASHBOARD_NAME, [
        {
          label: USER_FILTER_LABEL,
          value: audienceKey,
        },
        {
          label: INST_FILTER_LABEL,
          value: gdGQLData.institution.instGuid,
        },
      ]);
      setPlatformInfoEventValue(dispatch, false);
    }
  }, [platformInfoEvent]);

  // Loading timeout to ensure we will show GoodData even if we have an event issue
  setTimeout(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, 5000);

  return (
    <Card
      overrideStyles={{
        width: '803px',
        height: '791px',
        h2MarginBottom: '0px',
        paddingTop: '0px',
        paddingRight: '0px',
        paddingBottom: '0px',
        paddingLeft: '0px',
      }}
    >
      {isLoading && (
        <div className={classes.loader}>
          <div className={classes.center}>
            <Loading />
          </div>
        </div>
      )}
      <GoodDataDashboardContainer
        title="Profile Details Dashboard"
        name={PROFILE_DETAILS_DASHBOARD_NAME}
        id={PROFILE_DETAILS_DASHBOARD_NAME}
        gdData={gdGQLData}
        width="803px"
        height="791px"
        border="none"
        errorContainerStyles={{
          width: '803px',
          height: '791px',
        }}
        goodDataDropdownStyling={{
          height: '62px',
          width: '170px',
          backgroundColor: '#FFFFFF',
          position: 'relative',
          marginBottom: '-62px',
          zIndex: 8,
          top: '60px',
          left: '615px',
        }}
        lockScrolling
      />
    </Card>
  );
};

ProfileDetails.defaultProps = {
  classes: {},
};

export default withErrorBoundary(injectSheet(styleSheet)(ProfileDetails));
